import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Reservation, Listing, FilterState, KPIValues, SavedView, SortConfig,
  computeADR, computeLeadTime, QuickFilter, deriveClientCode, ReservationSource, ViewMode,
} from "@/types/reservation";
import { getAuthenticatedCrmClient, getLocalAccessToken, ensureCrmTokenFresh, ensureLocalTokenFresh } from "@/lib/supabase-clients";
import { useQuery } from "@tanstack/react-query";
import { addDays, subDays, startOfDay, format, startOfMonth, endOfMonth, subMonths, addMonths } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useUserListingAccess } from "@/hooks/use-user-listing-access";
import { useUserRole } from "@/hooks/use-user-role";
import { readSharedClientCodes, writeSharedClientCodes, readSharedPropertyIds, writeSharedPropertyIds, useSharedClientCodes, useSharedPropertyIds } from "@/lib/shared-client-filter";
import { isMuExcluded } from "@/lib/utils";


/**
 * Format a Date as a literal calendar day (YYYY-MM-DD) using LOCAL fields.
 * Avoids toISOString() which shifts to UTC and can off-by-one date filters.
 */
function toCalendarDate(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

// ─── Default filter state ────────────────────────────────────────────────────

const defaultFilters: FilterState = {
  dateMode: "stay",
  quickFilter: "next30",
  dateFrom: startOfDay(new Date()),
  dateTo: addDays(startOfDay(new Date()), 30),
  listings: [],
  clients: [],
  bookingStatuses: ["booked"],
  includeCancellations: false,
  search: "",
  listingStatus: "all",
};

// ─── Quick-filter date ranges ────────────────────────────────────────────────

function quickFilterDates(qf: QuickFilter): { from: Date; to: Date } | null {
  const today = startOfDay(new Date());
  switch (qf) {
    case "today": return { from: today, to: today };
    case "last7": return { from: subDays(today, 7), to: today };
    case "last15": return { from: subDays(today, 15), to: today };
    case "last30": return { from: subDays(today, 30), to: today };
    case "last90": return { from: subDays(today, 90), to: today };
    case "next30": return { from: today, to: addDays(today, 30) };
    case "next60": return { from: today, to: addDays(today, 60) };
    case "next90": return { from: today, to: addDays(today, 90) };
    case "allTime": return { from: new Date(1900, 0, 1), to: new Date(2099, 11, 31) };
    case "custom": return null;
  }
}

// ─── Server filter params ────────────────────────────────────────────────────

interface ServerFilters {
  dateMode: "booked" | "stay";
  dateFrom: string;
  dateTo: string;
  listings: string[];
  bookingStatuses: string[];
  includeCancellations: boolean;
  search: string;
}

// Map shared booking-status vocabulary (PriceLabs-style) to Guesty equivalents.
const GUESTY_STATUS_MAP: Record<string, string> = {
  booked: "confirmed",
  Booked: "confirmed",
  Cancelled: "canceled",
  cancelled: "canceled",
  Confirmed: "confirmed",
};
function toPricelabsStatus(guestyStatus: string | null | undefined): string {
  const s = (guestyStatus || "").toLowerCase();
  if (!s) return "";
  if (s === "confirmed") return "booked";
  if (s === "canceled" || s === "cancelled") return "Cancelled";
  return guestyStatus as string;
}

// Expand cancel variants so the IN(...) payload matches rows regardless of
// source spelling: Guesty stores "canceled" (1 L), PriceLabs stores "cancelled" (2 Ls).
function expandCancelVariants(statuses: string[]): string[] {
  const out = new Set(statuses);
  const hasCancel = statuses.some((s) => {
    const l = s.toLowerCase();
    return l === "canceled" || l === "cancelled";
  });
  if (hasCancel) {
    out.add("canceled");
    out.add("cancelled");
  }
  return Array.from(out);
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ─── Fetch functions ─────────────────────────────────────────────────────────

/**
 * Proxy call for local-only users (no CRM token). The edge function validates
 * the local JWT, resolves ACL from local user_roles+user_owner_access, and
 * queries CRM with the service role — same row shape as the direct path.
 */
async function callCrmProxy(action: string, payload: Record<string, unknown>): Promise<any[]> {
  await ensureLocalTokenFresh();
  const localToken = getLocalAccessToken();
  if (!localToken) throw new Error("Local session not ready");
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-read`;
  const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: anon,
      Authorization: `Bearer ${localToken}`,
    },
    body: JSON.stringify({ action, ...payload }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`crm-read ${action} failed: ${res.status} ${t}`);
  }
  const json = await res.json();
  return (json?.rows as any[]) ?? [];
}

function mapPricelabsRows(data: any[]): Reservation[] {
  return (data || []).map((r): Reservation => ({
    ...r,
    total_cost_bup: Number(r.total_cost ?? 0),
    rental_revenue: Number(r.rental_revenue ?? 0),
    no_of_days: Number(r.no_of_days ?? 0),
    property_id: r.property_id ?? null,
    source: r.pms ?? null,
    system_source: "pricelabs",
  }));
}

function mapGuestyRows(data: any[], ownerMode = false): Reservation[] {
  return (data || []).map((r: any): Reservation => {
    const cancelled = !!r.canceled_at;
    // Single id namespace everywhere: CRM property_id. The owner proxy scopes
    // server-side by property_id too, so no guesty_listing_id override.
    const listingKey = r.property_id ?? null;
    return {
      id: r.reservation_id,
      created_at: r.created_at,
      listing: r.guest_name || r.property_id || "",
      booked_date: r.confirmed_at ? String(r.confirmed_at).slice(0, 10) : "",
      reservation_id: r.reservation_id,
      check_in: r.check_in,
      check_out: r.check_out,
      booking_status: cancelled ? "Cancelled" : toPricelabsStatus(r.status),
      rental_revenue: Number(r.accommodation_fare_adjusted ?? r.net_income ?? 0),
      no_of_days: Number(r.nights_count ?? 0),
      currency: r.currency || "",
      total_cost_bup: Number(r.net_income ?? 0),
      blt: null,
      cancelled_on: r.canceled_at ? String(r.canceled_at).slice(0, 10) : null,
      property_id: listingKey,
      source: r.source ?? null,
      confirmation_code: r.confirmation_code ?? null,
      system_source: "guesty",
    };
  });
}

async function fetchReservationsPricelabs(sf: ServerFilters, limit = 5000, ownerMode = false, forceProxy = false): Promise<Reservation[]> {
  if (!sf.bookingStatuses.length) return [];
  if (!forceProxy && !ownerMode) await ensureCrmTokenFresh();
  const client = forceProxy || ownerMode ? null : getAuthenticatedCrmClient();
  if (!client || ownerMode) {
    // Owner/local-only/non-admin role → proxy path (service-role bypass of CRM RLS + local ACL).
    const rows = await callCrmProxy("reservations_pricelabs", {
      filters: {
        dateMode: sf.dateMode,
        dateFrom: sf.dateFrom,
        dateTo: sf.dateTo,
        listings: sf.listings,
        bookingStatuses: expandCancelVariants(sf.bookingStatuses),
        includeCancellations: sf.includeCancellations,
        search: sf.search,
      },
      limit,
    });
    return mapPricelabsRows(rows);
  }
  let query = client
    .from("pricelabs_reservations")
    .select("id, created_at, listing, booked_date, reservation_id, check_in, check_out, booking_status, rental_revenue, no_of_days, currency, total_cost, blt, cancelled_on, property_id, pms");

  if (sf.dateMode === "booked") {
    query = query.gte("booked_date", sf.dateFrom).lte("booked_date", sf.dateTo);
  } else {
    query = query.lte("check_in", sf.dateTo).gte("check_out", sf.dateFrom);
  }
  if (sf.listings.length) query = query.in("property_id", sf.listings);
  query = query.in("booking_status", expandCancelVariants(sf.bookingStatuses));
  const selectsCancelledPL = sf.bookingStatuses.some((s) => s.toLowerCase() === "cancelled" || s.toLowerCase() === "canceled");
  if (!sf.includeCancellations && !selectsCancelledPL) query = query.neq("booking_status", "cancelled");

  if (sf.search) {
    const q = sf.search.replace(/[%_\\]/g, "\\$&");
    query = query.or(`listing.ilike.%${q}%,reservation_id.ilike.%${q}%,booking_status.ilike.%${q}%`);
  }

  const { data, error } = await query.limit(limit);
  if (error) throw new Error(error.message);
  return mapPricelabsRows((data as any[]) || []);
}

async function fetchReservationsGuesty(sf: ServerFilters, limit = 5000, ownerMode = false, forceProxy = false): Promise<Reservation[]> {
  if (!sf.bookingStatuses.length) return [];
  if (!forceProxy && !ownerMode) await ensureCrmTokenFresh();
  const client = forceProxy ? null : getAuthenticatedCrmClient();
  if (!client || ownerMode) {
    // Owner mode always uses the proxy (server-side guesty_listing_id scoping).
    const mapped = Array.from(new Set(sf.bookingStatuses.map((s) => GUESTY_STATUS_MAP[s] ?? s)));
    const rows = await callCrmProxy("reservations_guesty", {
      filters: {
        dateMode: sf.dateMode,
        dateFrom: sf.dateFrom,
        dateTo: sf.dateTo,
        listings: sf.listings,
        bookingStatuses: expandCancelVariants(mapped),
        includeCancellations: sf.includeCancellations,
        search: sf.search,
      },
      limit,
    });
    return mapGuestyRows(rows, ownerMode);
  }
  let query = client
    .from("guesty_reservations")
    .select("reservation_id, confirmation_code, confirmed_at, check_in, check_out, status, accommodation_fare_adjusted, nights_count, currency, net_income, canceled_at, property_id, guest_name, created_at, source");

  if (sf.dateMode === "booked") {
    query = query
      .gte("confirmed_at", `${sf.dateFrom}T00:00:00+00:00`)
      .lte("confirmed_at", `${sf.dateTo}T23:59:59+00:00`);
  } else {
    query = query.lte("check_in", sf.dateTo).gte("check_out", sf.dateFrom);
  }
  if (sf.listings.length) query = query.in("property_id", sf.listings);
  const selectsCancelledG = sf.bookingStatuses.some((s) => s.toLowerCase() === "cancelled" || s.toLowerCase() === "canceled");
  {
    const mapped = Array.from(new Set(sf.bookingStatuses.map((s) => GUESTY_STATUS_MAP[s] ?? s)));
    query = query.in("status", expandCancelVariants(mapped));
  }
  if (!sf.includeCancellations && !selectsCancelledG) query = query.is("canceled_at", null);

  if (sf.search) {
    const q = sf.search.replace(/[%_\\]/g, "\\$&");
    query = query.or(`guest_name.ilike.%${q}%,reservation_id.ilike.%${q}%,status.ilike.%${q}%`);
  }

  const { data, error } = await query.limit(limit);
  if (error) throw new Error(error.message);
  return mapGuestyRows(data || []);
}

async function fetchReservationsPartitioned(
  sf: ServerFilters,
  pricelabsIds: string[],
  guestyIds: string[],
  limit?: number,
  ownerMode = false,
  forceProxy = false,
): Promise<Reservation[]> {
  const tasks: Promise<Reservation[]>[] = [];
  if (pricelabsIds.length) tasks.push(fetchReservationsPricelabs({ ...sf, listings: pricelabsIds }, limit, ownerMode, forceProxy));
  if (guestyIds.length) tasks.push(fetchReservationsGuesty({ ...sf, listings: guestyIds }, limit, ownerMode, forceProxy));
  if (tasks.length === 0) return [];
  const parts = await Promise.all(tasks);
  return parts.flat();
}

async function fetchListings(ownerMode = false): Promise<Listing[]> {
  if (!ownerMode) await ensureCrmTokenFresh();
  const client = getAuthenticatedCrmClient();
  let raw: any[];
  if (client && !ownerMode) {
    const { data, error } = await client
      .from("property")
      .select("property_id, client_code, push_enabled, nickname, source")
      .order("property_id", { ascending: true })
      .limit(100000);
    if (error) throw error;
    raw = data ?? [];
    // Fallback for client-role users whose CRM session RLS filters everything
    // out — route through the crm-read proxy (service role + local ACL).
    if (raw.length === 0) {
      try {
        raw = await callCrmProxy("listings", {});
      } catch { /* keep empty */ }
    }
  } else {
    // Owners always go through the proxy so server-side ACL applies.
    raw = await callCrmProxy("listings", {});
  }
  raw = raw.filter((r: any) => !isMuExcluded(r?.nickname));

  return raw.map((r: any): Listing => {
    // Canonical listing identifier is always the CRM property_id — it is what
    // the crm-read proxy filters reservations on for both sources.
    const key = r.property_id;
    return {
      id: key,
      code: r.nickname ?? key,
      client_code: r.client_code ?? null,
      pricelabs_id: r.property_id,
      property_id: key,
      push_enabled: r.push_enabled ?? null,
      nickname: r.nickname ?? null,
      
      source: r.source === "guesty" || r.source === "pricelabs" ? r.source : null,
    };
  });
}


// Fast distinct client_code fetch via edge function (keeps the project's existing pattern).
async function fetchDistinctClientCodes(): Promise<string[]> {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/crm-distinct-client-codes`;
  const { getLocalAccessToken } = await import("@/lib/supabase-clients");
  const localToken = getLocalAccessToken();
  if (!localToken) throw new Error("distinct clients fetch failed: no local token");
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localToken}`,
    },
  });
  if (res.status === 403) return [];
  if (!res.ok) throw new Error(`distinct clients fetch failed: ${res.status}`);
  const json = await res.json();
  return ((json.clients ?? json.owners) as string[]) ?? [];
}


// ─── Context type ────────────────────────────────────────────────────────────

interface ReservationsContextType {
  allData: Reservation[];
  calendarData: Reservation[];
  filteredData: Reservation[];
  kpis: KPIValues;
  isLoading: boolean;
  error: string | null;

  filters: FilterState;
  setFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  setFilters: (partial: Partial<FilterState>) => void;
  resetFilters: () => void;

  sorts: SortConfig[];
  setSorts: React.Dispatch<React.SetStateAction<SortConfig[]>>;

  page: number;
  pageSize: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  paginatedData: Reservation[];
  totalPages: number;

  distinctListings: { value: string; label: string }[];
  distinctClients: string[];
  distinctStatuses: string[];
  listingLookup: Map<string, Listing>;

  selectedReservation: Reservation | null;
  setSelectedReservation: React.Dispatch<React.SetStateAction<Reservation | null>>;

  savedViews: SavedView[];
  saveView: (name: string) => void;
  loadView: (id: string) => void;
  deleteView: (id: string) => void;

  viewMode: ViewMode;
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;

  needsClientSelection: boolean;

  dataDateExtent: { min: Date | null; max: Date | null };
}

const ReservationsContext = createContext<ReservationsContextType | null>(null);

export function useReservations() {
  const ctx = useContext(ReservationsContext);
  if (!ctx) throw new Error("useReservations must be used within ReservationsProvider");
  return ctx;
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function ReservationsProvider({ children }: { children: React.ReactNode }) {
  const { user, crmTokenReady, authSource, localToken } = useAuth();
  const isAuthenticated = !!user;
  const canUseProxy = authSource === "local" || !!localToken;
  const canReadCrm = crmTokenReady || canUseProxy;

  // Seed filters synchronously from the shared cross-page store so the
  // restored selection is visible on first paint (no empty flash, no double
  // data fetch when the user navigates between pages).
  const [filters, setFiltersState] = useState<FilterState>(() => {
    const sharedOwners = readSharedClientCodes();
    const sharedProps = readSharedPropertyIds();
    let sharedStatus: FilterState["listingStatus"] = "listed";
    if (typeof window !== "undefined") {
      const v = window.sessionStorage.getItem("ownerFilters:propertyStatus:v1");
      if (v === "all" || v === "listed" || v === "unlisted") sharedStatus = v;
    }
    return {
      ...defaultFilters,
      clients: sharedOwners ?? defaultFilters.clients,
      listings: sharedProps ?? defaultFilters.listings,
      listingStatus: sharedStatus,
    };
  });
  const [sorts, setSorts] = useState<SortConfig[]>([{ key: "booked_date", direction: "desc" }]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => {
    try { return JSON.parse(localStorage.getItem("res_saved_views") || "[]"); }
    catch { return []; }
  });

  // Debounce search
  const debouncedSearch = useDebouncedValue(filters.search, 300);

  // ACL: which owner codes can this user see?
  const { allowedClientCodes, allowedPropertyIds, isLoading: aclLoading, isReady: aclReady } = useUserListingAccess({ includeListingIds: true });
  const { isAdmin, roles, isSuperAdmin, primaryRole, isLoading: roleLoading } = useUserRole();
  const isOwner = (roles.includes("owner") || roles.includes("owner_test")) && !isSuperAdmin && !isAdmin;
  const isClient = primaryRole === "client" || primaryRole === "client_test";
  const aclAppliedRef = useRef(false);
  const sharedClientCodes = useSharedClientCodes();
  const sharedPropertyIds = useSharedPropertyIds();

  // Initial seed for client filter — runs once when role + ACL become ready.
  useEffect(() => {
    if (aclAppliedRef.current || !aclReady) return;
    if (isClient) {
      // Client / client_test: always seed from ACL. Ignore any stale shared
      // filter (e.g. left over from a previous admin session in the same tab).
      if (allowedClientCodes && allowedClientCodes.size > 0) {
        setFiltersState((prev) => ({ ...prev, clients: Array.from(allowedClientCodes) }));
      }
    } else if (!isAdmin && !isOwner) {
      // AM and other non-admin, non-owner roles: shared overrides ACL.
      const shared = readSharedClientCodes();
      if (shared !== null) {
        setFiltersState((prev) => ({ ...prev, clients: shared }));
      } else if (allowedClientCodes && allowedClientCodes.size > 0) {
        setFiltersState((prev) => ({ ...prev, clients: Array.from(allowedClientCodes) }));
      }
    } else if (isAdmin && !isOwner) {
      const shared = readSharedClientCodes();
      if (shared !== null) {
        setFiltersState((prev) => ({ ...prev, clients: shared }));
      }
    }
    aclAppliedRef.current = true;
  }, [allowedClientCodes, aclReady, isAdmin, isOwner, isClient]);

  // Reflect cross-page changes to the shared client filter (made on Performance,
  // Calendar, Guest Ratings, …) into this always-mounted provider. Skip for
  // client/owner roles whose owner set is locked by ACL.
  useEffect(() => {
    if (!aclAppliedRef.current) return;
    if (isClient || isOwner) return;
    if (sharedClientCodes === null) return;
    setFiltersState((prev) => {
      if (prev.clients.length === sharedClientCodes.length && prev.clients.every((c, i) => c === sharedClientCodes[i])) return prev;
      return { ...prev, clients: sharedClientCodes };
    });
  }, [sharedClientCodes, isClient, isOwner]);

  // Write-back shared client filter whenever the user changes it.
  // Skip for client / client_test so their per-user ACL set never pollutes the
  // cross-page shared key used by admins/AM on the same tab.
  useEffect(() => {
    if (!aclAppliedRef.current) return;
    if (isOwner || isClient) return;
    writeSharedClientCodes(filters.clients);
  }, [filters.clients, isOwner, isClient]);

  // ─── Shared property filter (admin/AM/client only; owner uses guesty_id namespace) ──
  const propsBootstrappedRef = useRef(false);
  const pendingPropertyBootstrapRef = useRef<string[] | null>(null);

  // ─── Listings (phase 1) ────────────────────────────────────────────────────

  const { data: listings = [], isLoading: listingsLoading, error: listingsError } = useQuery<Listing[]>({
    queryKey: ["crm_property_listings", canReadCrm, authSource, isOwner],
    queryFn: () => fetchListings(isOwner),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: isAuthenticated && canReadCrm,
  });

  // Bootstrap shared property selection. The initial state already seeded
  // synchronously from `readSharedPropertyIds()`; this effect's only job
  // is to prune IDs that don't belong to the listings the user can see,
  // once listings have actually loaded.
  useEffect(() => {
    if (propsBootstrappedRef.current) return;
    if (isOwner) { propsBootstrappedRef.current = true; return; }
    if (!listings || listings.length === 0) return;
    const allowed = new Set(listings.map((l) => l.property_id).filter(Boolean) as string[]);
    if (readSharedPropertyIds() === null) {
      // Fresh session: default to listed-only (fallback to all if none flagged).
      const listed = listings.filter((l) => l.push_enabled === true).map((l) => l.property_id).filter(Boolean) as string[];
      const seed = listed.length > 0 ? listed : (listings.map((l) => l.property_id).filter(Boolean) as string[]);
      pendingPropertyBootstrapRef.current = seed;
      setFiltersState((prev) => (arraysEqual(prev.listings, seed) ? prev : { ...prev, listings: seed }));
      writeSharedPropertyIds(seed);
    } else {
      setFiltersState((prev) => {
        const intersected = prev.listings.filter((id) => allowed.has(id));
        pendingPropertyBootstrapRef.current = intersected;
        if (intersected.length === prev.listings.length) return prev;
        return { ...prev, listings: intersected };
      });
    }
    propsBootstrappedRef.current = true;
  }, [isOwner, listings]);

  // Reflect cross-page property changes mid-life.
  useEffect(() => {
    if (!propsBootstrappedRef.current) return;
    if (isOwner) return;
    if (sharedPropertyIds === null) return;
    if (!listings || listings.length === 0) return;
    const allowed = new Set(listings.map((l) => l.property_id).filter(Boolean) as string[]);
    const intersected = sharedPropertyIds.filter((id) => allowed.has(id));
    setFiltersState((prev) => {
      if (prev.listings.length === intersected.length && prev.listings.every((c, i) => c === intersected[i])) return prev;
      return { ...prev, listings: intersected };
    });
  }, [sharedPropertyIds, listings, isOwner]);

  // Write-back shared property filter when admin/AM narrows the listing picker.
  // Preserve any IDs that belong to listings outside this page's universe so we
  // never silently shrink the cross-page selection.
  useEffect(() => {
    if (!propsBootstrappedRef.current) return;
    if (isOwner) return;
    const pending = pendingPropertyBootstrapRef.current;
    if (pending && !arraysEqual(filters.listings, pending)) return;
    if (pending && arraysEqual(filters.listings, pending)) {
      pendingPropertyBootstrapRef.current = null;
    }
    const known = new Set(listings.map((l) => l.property_id).filter(Boolean) as string[]);
    const shared = readSharedPropertyIds() ?? [];
    const preservedForeign = shared.filter((id) => !known.has(id));
    const nextShared = [...preservedForeign, ...filters.listings];
    if (arraysEqual(shared, nextShared)) return;
    writeSharedPropertyIds(nextShared);
  }, [filters.listings, listings, isOwner]);

  // Sync the listing-status sub-tab with the same sessionStorage key used by
  // PropertyPill on other pages, so the pill matches across the whole app.
  useEffect(() => {
    if (isOwner) return;
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem("ownerFilters:propertyStatus:v1", filters.listingStatus);
    } catch { /* ignore */ }
  }, [filters.listingStatus, isOwner]);







  // Distinct client_codes via edge function. Skipped for owners (they don't use client_code).
  const { data: allClientCodes = [] } = useQuery({
    queryKey: ["crm_distinct_client_codes"],
    queryFn: fetchDistinctClientCodes,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: isAuthenticated && !roleLoading && ["super_admin", "admin", "am", "rm"].includes(primaryRole ?? ""),
  });

  if (listingsError) {
    console.error("[ReservationsContext] CRM property query failed:", listingsError);
  }

  const listingLookup = useMemo(() => {
    const map = new Map<string, Listing>();
    listings.forEach((l) => { if (l.property_id) map.set(l.property_id, l); });
    return map;
  }, [listings]);

  // ─── ACL-filtered listings ─────────────────────────────────────────────────

  const aclFilteredListings = useMemo(() => {
    if (allowedClientCodes === null) return listings; // super_admin
    // Owners: the listings array is already ACL-scoped server-side (proxy filtered
    // by user_listing_access.listing_id), so don't re-filter by client_code.
    if (isOwner) return listings;
    return listings.filter((l) => {
      const oc = deriveClientCode(l);
      return oc ? allowedClientCodes.has(oc) : false;
    });
  }, [listings, allowedClientCodes, isOwner]);

  const aclLookup = useMemo(() => {
    const map = new Map<string, Listing>();
    aclFilteredListings.forEach((l) => { if (l.property_id) map.set(l.property_id, l); });
    return map;
  }, [aclFilteredListings]);

  // ─── Resolve owner filter → property_ids ───────────────────────────────────

  // Helper: filter a list of property_ids by the active listingStatus
  // (Listed / Unlisted / All). Looks each ID up in the listing universe.
  const filterByListingStatus = useCallback((ids: string[]): string[] => {
    if (filters.listingStatus === "all") return ids;
    const wantListed = filters.listingStatus === "listed";
    return ids.filter((id) => {
      const meta = listingLookup.get(id);
      if (!meta) return false;
      return wantListed ? meta.push_enabled === true : meta.push_enabled === false;
    });
  }, [filters.listingStatus, listingLookup]);

  const ownerPropertyIds = useMemo(() => {
    if (isOwner) return [];
    if (!filters.clients.length) return [];
    const ids = listings
      .filter((l) => l.client_code && filters.clients.includes(l.client_code))
      .map((l) => l.property_id)
      .filter(Boolean) as string[];
    const unique = Array.from(new Set(ids));
    return filterByListingStatus(unique);
  }, [filters.clients, listings, isOwner, filterByListingStatus]);

  const statusIsExplicit = filters.listingStatus !== "all";

  const combinedListingFilter = useMemo(() => {
    if (isOwner) return filters.listings;
    const fromListings = filters.listings;
    const fromOwners = ownerPropertyIds;
    if (fromListings.length && fromOwners.length) {
      const ownerSet = new Set(fromOwners);
      return fromListings.filter((id) => ownerSet.has(id));
    }
    if (fromListings.length) return fromListings;
    if (fromOwners.length) return fromOwners;
    return [];
  }, [filters.listings, ownerPropertyIds, isOwner]);

  // ─── Build server filters ─────────────────────────────────────────────────

  // Partition the active listing set by each property's source.
  // When no listings/owners are explicitly selected, fall back to the ACL-allowed
  // listings (or all listings for super_admin) so we still know which source bucket
  // each property belongs to and can avoid issuing an unscoped query against the
  // wrong reservations table.
  const hasExplicitSelection = filters.clients.length > 0 || filters.listings.length > 0;
  const needsClientSelection = !isOwner && !hasExplicitSelection;

  const sourcePartition = useMemo(() => {
    // Owners: partition by each property's source (guesty/pricelabs) using
    // the listingLookup populated from the CRM `property` rows returned by
    // the listings action. Fall back to the ACL-allowed listings when no
    // explicit selection is made.
    if (isOwner) {
      let baseIds: string[];
      if (combinedListingFilter.length) {
        baseIds = combinedListingFilter;
      } else {
        // Status is explicit (Listed/Unlisted) + user selection is empty →
        // honor the status against the ACL universe instead of falling back
        // to "all", which would surface data the pill says to exclude.
        const fallback = aclFilteredListings.length
          ? aclFilteredListings.map((l) => l.property_id).filter((id): id is string => !!id)
          : Array.from(allowedPropertyIds ?? []) as string[];
        baseIds = statusIsExplicit ? filterByListingStatus(fallback) : fallback;
      }
      const pricelabsIds: string[] = [];
      const guestyIds: string[] = [];
      for (const id of baseIds) {
        const src = listingLookup.get(id)?.source;
        if (src === "pricelabs") pricelabsIds.push(id);
        else guestyIds.push(id); // default unknown/guesty → guesty bucket
      }
      return { pricelabsIds, guestyIds };
    }
    // Admin & super_admin: require an explicit client/listing selection before
    // issuing any reservation query. Prevents oversized property_id=in.(...) URLs.
    if (!hasExplicitSelection) {
      return { pricelabsIds: [] as string[], guestyIds: [] as string[] };
    }
    // Explicit selection but the status filter pruned everything away → return
    // empty buckets so the query short-circuits to [] instead of silently
    // ignoring the status.
    if (combinedListingFilter.length === 0) {
      return { pricelabsIds: [] as string[], guestyIds: [] as string[] };
    }
    const baseIds = combinedListingFilter;
    const pricelabsIds: string[] = [];
    const guestyIds: string[] = [];
    for (const id of baseIds) {
      const src = listingLookup.get(id)?.source;
      if (src === "guesty") guestyIds.push(id);
      else if (src === "pricelabs") pricelabsIds.push(id);
    }
    return { pricelabsIds, guestyIds };
  }, [combinedListingFilter, aclFilteredListings, listingLookup, isOwner, allowedPropertyIds, hasExplicitSelection, statusIsExplicit, filterByListingStatus]);

  const serverFilters = useMemo<ServerFilters>(() => {
    const from = filters.dateFrom ?? subDays(new Date(), 30);
    const to = filters.dateTo ?? new Date();
    return {
      dateMode: filters.dateMode,
      dateFrom: toCalendarDate(from),
      dateTo: toCalendarDate(to),
      listings: combinedListingFilter,
      bookingStatuses: filters.bookingStatuses,
      includeCancellations: filters.includeCancellations,
      search: debouncedSearch,
    };
  }, [filters.dateMode, filters.dateFrom, filters.dateTo, combinedListingFilter, filters.bookingStatuses, filters.includeCancellations, debouncedSearch]);

  // Non-admin/non-owner roles (client, client_test, am, …) must go through the
  // crm-read proxy because CRM-side RLS on reservation tables silently filters
  // their rows. Admins keep the direct CRM path.
  const forceProxy = !isSuperAdmin && !isAdmin && !isOwner;

  const reservationQueryKey = useMemo(() => [
    "crm_reservations",
    serverFilters.dateMode,
    serverFilters.dateFrom,
    serverFilters.dateTo,
    [...sourcePartition.pricelabsIds].sort().join("|"),
    [...sourcePartition.guestyIds].sort().join("|"),
    [...serverFilters.bookingStatuses].sort().join("|"),
    serverFilters.includeCancellations ? "include-cancelled" : "exclude-cancelled",
    serverFilters.search,
    isOwner ? "owner" : "non-owner",
    forceProxy ? "proxy" : "direct",
  ], [
    serverFilters.dateMode,
    serverFilters.dateFrom,
    serverFilters.dateTo,
    serverFilters.bookingStatuses,
    serverFilters.includeCancellations,
    serverFilters.search,
    sourcePartition.pricelabsIds,
    sourcePartition.guestyIds,
    isOwner,
    forceProxy,
  ]);

  // Cancel Rate KPI must ignore the booking-status filter so the rate
  // reflects "cancellations across all reservations" regardless of UI status.
  const cancelRateServerFilters = useMemo<ServerFilters>(() => ({
    ...serverFilters,
    bookingStatuses: ["booked", "cancelled"],
    includeCancellations: true,
  }), [serverFilters]);

  const cancelRateQueryKey = useMemo(() => [
    "crm_reservations_cancel_rate",
    cancelRateServerFilters.dateMode,
    cancelRateServerFilters.dateFrom,
    cancelRateServerFilters.dateTo,
    [...sourcePartition.pricelabsIds].sort().join("|"),
    [...sourcePartition.guestyIds].sort().join("|"),
    [...cancelRateServerFilters.bookingStatuses].sort().join("|"),
    cancelRateServerFilters.includeCancellations ? "include-cancelled" : "exclude-cancelled",
    cancelRateServerFilters.search,
    isOwner ? "owner" : "non-owner",
    forceProxy ? "proxy" : "direct",
  ], [
    cancelRateServerFilters.dateMode,
    cancelRateServerFilters.dateFrom,
    cancelRateServerFilters.dateTo,
    cancelRateServerFilters.bookingStatuses,
    cancelRateServerFilters.includeCancellations,
    cancelRateServerFilters.search,
    sourcePartition.pricelabsIds,
    sourcePartition.guestyIds,
    isOwner,
    forceProxy,
  ]);

  // ─── Reservations (phase 2) ────────────────────────────────────────────────

  const { data: allData = [], isLoading: resLoading, error: queryError } = useQuery({
    queryKey: reservationQueryKey,
    queryFn: () => fetchReservationsPartitioned(serverFilters, sourcePartition.pricelabsIds, sourcePartition.guestyIds, undefined, isOwner, forceProxy),
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled: isAuthenticated && canReadCrm && (isOwner || hasExplicitSelection),
  });

  const { data: cancelRateRaw = [] } = useQuery({
    queryKey: cancelRateQueryKey,
    queryFn: () => fetchReservationsPartitioned(cancelRateServerFilters, sourcePartition.pricelabsIds, sourcePartition.guestyIds, undefined, isOwner, forceProxy),
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled: isAuthenticated && canReadCrm && (isOwner || hasExplicitSelection),
  });

  // ─── Calendar dataset (independent of date filters) ───────────────────────

  const calendarServerFilters = useMemo<ServerFilters>(() => {
    const today = startOfDay(new Date());
    const from = startOfMonth(subMonths(today, 3));
    const to = endOfMonth(addMonths(today, 12));
    const selectsCancel = filters.bookingStatuses.some((s) => {
      const low = s.toLowerCase();
      return low === "canceled" || low === "cancelled";
    });
    return {
      dateMode: "stay",
      dateFrom: toCalendarDate(from),
      dateTo: toCalendarDate(to),
      listings: combinedListingFilter,
      bookingStatuses: filters.bookingStatuses,
      includeCancellations: selectsCancel,
      search: debouncedSearch,
    };
  }, [combinedListingFilter, debouncedSearch, filters.bookingStatuses]);

  const { data: calendarRaw = [] } = useQuery({
    queryKey: ["crm_reservations_calendar", calendarServerFilters, sourcePartition.pricelabsIds, sourcePartition.guestyIds, isOwner, forceProxy],
    queryFn: () => fetchReservationsPartitioned(calendarServerFilters, sourcePartition.pricelabsIds, sourcePartition.guestyIds, 100_000, isOwner, forceProxy),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: isAuthenticated && canReadCrm && viewMode === "calendar" && (isOwner || hasExplicitSelection),
  });

  const isLoading = resLoading || listingsLoading || aclLoading;

  // Only keep reservations whose property_id is in the ACL-filtered listings.
  // For owners, the proxy already scopes server-side by guesty_listing_id, so
  // skip the client-side property_id lookup (CRM `property` table may not
  // contain a row for every owned listing).
  const validData = useMemo(() => {
    if (isOwner) return allData;
    if (!aclFilteredListings.length) return allData;
    return allData.filter((r) => r.property_id && aclLookup.has(r.property_id));
  }, [allData, aclFilteredListings, aclLookup, isOwner]);

  const cancelRateData = useMemo(() => {
    if (isOwner) return cancelRateRaw;
    if (!aclFilteredListings.length) return cancelRateRaw;
    return cancelRateRaw.filter((r) => r.property_id && aclLookup.has(r.property_id));
  }, [cancelRateRaw, aclFilteredListings, aclLookup, isOwner]);

  // Calendar dataset post-filter
  const calendarData = useMemo(() => {
    const base = (isOwner || !aclFilteredListings.length)
      ? calendarRaw
      : calendarRaw.filter((r) => r.property_id && aclLookup.has(r.property_id));
    if (filters.bookingStatuses.length > 0) {
      const set = new Set(
        filters.bookingStatuses.map((s) => {
          const low = s.toLowerCase();
          if (low === "canceled" || low === "cancelled") return "cancelled";
          if (low === "confirmed" || low === "booked") return "booked";
          return low;
        })
      );
      return base.filter((r) => {
        const s = (r.booking_status || "").toLowerCase();
        const norm = s.includes("cancel") ? "cancelled" : (s === "confirmed" ? "booked" : s);
        return set.has(norm);
      });
    }
    return base.filter((r) => {
      const s = (r.booking_status || "").toLowerCase();
      if (s.includes("cancel")) return false;
      if (r.cancelled_on) return false;
      return true;
    });
  }, [calendarRaw, aclFilteredListings, aclLookup, filters.bookingStatuses, isOwner]);

  // ─── Filter setters ───────────────────────────────────────────────────────

  const listingsRef = useRef<Listing[]>([]);
  useEffect(() => { listingsRef.current = aclFilteredListings; }, [aclFilteredListings]);

  const setFilter = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFiltersState((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "quickFilter") {
        const dates = quickFilterDates(value as QuickFilter);
        if (dates) { next.dateFrom = dates.from; next.dateTo = dates.to; }
      }
      if (key === "listingStatus") {
        const status = value as FilterState["listingStatus"];
        if (status === "all") {
          next.listings = [];
        } else {
          const ownerScoped = prev.clients.length > 0
            ? listingsRef.current.filter((l) => l.client_code && prev.clients.includes(l.client_code))
            : listingsRef.current;
          const wantTrue = status === "listed";
          const ids = ownerScoped
            .filter((l) => l.push_enabled === wantTrue)
            .map((l) => l.property_id)
            .filter((id): id is string => !!id);
          next.listings = Array.from(new Set(ids));
        }
      }
      return next;
    });
    setPage(1);
  }, []);

  const setFilters = useCallback((partial: Partial<FilterState>) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    const clients = isOwner
      ? defaultFilters.clients
      : (allowedClientCodes && allowedClientCodes.size > 0
          ? Array.from(allowedClientCodes)
          : defaultFilters.clients);
    setFiltersState({ ...defaultFilters, clients });
    setPage(1);
  }, [allowedClientCodes, isOwner]);

  // ─── Distinct values ───────────────────────────────────────────────────────

  const distinctListings = useMemo(() => {
    let filtered = filters.clients.length > 0
      ? aclFilteredListings.filter((l) => l.client_code && filters.clients.includes(l.client_code))
      : aclFilteredListings;
    if (filters.listingStatus === "listed") {
      filtered = filtered.filter((l) => l.push_enabled === true);
    } else if (filters.listingStatus === "unlisted") {
      filtered = filtered.filter((l) => l.push_enabled === false);
    }
    const items = filtered
      .filter((l) => !!l.property_id)
      .map((l) => {
        const label = l.nickname || l.property_id!;
        return { value: l.property_id as string, label };
      });
    const seen = new Set<string>();
    const deduped = items.filter((i) => (seen.has(i.value) ? false : (seen.add(i.value), true)));
    return deduped.sort((a, b) => a.label.localeCompare(b.label));
  }, [aclFilteredListings, filters.clients, filters.listingStatus]);

  // Prune selected listings that are no longer valid after Client filter changes
  useEffect(() => {
    if (filters.listings.length === 0) return;
    const allowed = new Set(distinctListings.map((d) => d.value));
    const next = filters.listings.filter((id) => allowed.has(id));
    if (next.length !== filters.listings.length) {
      setFilter("listings", next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distinctListings]);

  const distinctClients = useMemo(() => {
    if (isOwner) return [];
    // Restrict to ACL-allowed codes when not super_admin.
    const base = allowedClientCodes === null
      ? allClientCodes
      : allClientCodes.filter((c) => allowedClientCodes.has(c));
    return [...new Set(
      base.filter((c): c is string => !!c && c.toLowerCase() !== "main")
    )].sort();
  }, [allClientCodes, allowedClientCodes, isOwner]);

  // Unified status vocabulary. Guesty's "confirmed"/"canceled" are mapped to
  // "booked"/"Cancelled" downstream, so the user-facing options are unified here.
  const distinctStatuses = useMemo(() => ["booked", "cancelled"], []);

  // ─── Sorting (client-side) ─────────────────────────────────────────────────

  const sortedData = useMemo(() => {
    if (!sorts.length) return validData;
    return [...validData].sort((a, b) => {
      for (const { key, direction } of sorts) {
        const aVal = (a as any)[key];
        const bVal = (b as any)[key];
        const cmp = typeof aVal === "number" ? aVal - bVal : String(aVal ?? "").localeCompare(String(bVal ?? ""));
        if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
      }
      return 0;
    });
  }, [validData, sorts]);

  // ─── KPIs ──────────────────────────────────────────────────────────────────

  const kpis = useMemo<KPIValues>(() => {
    const d = validData;
    const total = d.length;
    const roomNights = d.reduce((s, r) => s + (r.no_of_days || 0), 0);
    const revenue = d.reduce((s, r) => s + (r.rental_revenue || 0), 0);
    const adr = computeADR(revenue, roomNights);
    const avgLos = total ? roomNights / total : 0;
    // Cancel Rate ignores the booking-status filter — uses its own dataset.
    const crTotal = cancelRateData.length;
    const crCancelled = cancelRateData.filter((r) => r.booking_status === "Cancelled" || r.cancelled_on).length;
    const cancelRate = crTotal ? (crCancelled / crTotal) * 100 : 0;
    const leads = d.filter((r) => r.booked_date && r.check_in).map((r) => computeLeadTime(r.booked_date, r.check_in));
    const avgLeadTime = leads.length ? leads.reduce((s, l) => s + l, 0) / leads.length : 0;
    const totalCostBup = d.reduce((s, r) => s + (r.total_cost_bup || 0), 0);
    return { reservations: total, roomNights, revenue, adr, avgLos, cancelRate, avgLeadTime, totalCostBup };
  }, [validData, cancelRateData]);

  // ─── Data date extent (used by "All time" label) ──────────────────────────
  const dataDateExtent = useMemo<{ min: Date | null; max: Date | null }>(() => {
    let minStr: string | null = null;
    let maxStr: string | null = null;
    for (const r of validData) {
      const ci = r.check_in ? String(r.check_in).slice(0, 10) : null;
      const co = r.check_out ? String(r.check_out).slice(0, 10) : null;
      if (ci && /^\d{4}-\d{2}-\d{2}$/.test(ci) && (!minStr || ci < minStr)) minStr = ci;
      if (co && /^\d{4}-\d{2}-\d{2}$/.test(co) && (!maxStr || co > maxStr)) maxStr = co;
    }
    const toUTC = (s: string) => {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d));
    };
    return { min: minStr ? toUTC(minStr) : null, max: maxStr ? toUTC(maxStr) : null };
  }, [validData]);

  // ─── Pagination ────────────────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = useMemo(
    () => sortedData.slice((page - 1) * pageSize, page * pageSize),
    [sortedData, page, pageSize]
  );

  // ─── Saved views ───────────────────────────────────────────────────────────

  const persistViews = (views: SavedView[]) => {
    setSavedViews(views);
    localStorage.setItem("res_saved_views", JSON.stringify(views));
  };
  const saveView = (name: string) => {
    const view: SavedView = { id: crypto.randomUUID(), name, filters: { ...filters }, createdAt: new Date().toISOString() };
    persistViews([...savedViews, view]);
  };
  const loadView = (id: string) => {
    const view = savedViews.find((v) => v.id === id);
    if (view) {
      setFiltersState({
        ...view.filters,
        dateFrom: view.filters.dateFrom ? new Date(view.filters.dateFrom) : null,
        dateTo: view.filters.dateTo ? new Date(view.filters.dateTo) : null,
      });
      setPage(1);
    }
  };
  const deleteView = (id: string) => persistViews(savedViews.filter((v) => v.id !== id));

  return (
    <ReservationsContext.Provider
      value={{
        allData: validData, calendarData, filteredData: sortedData, kpis, isLoading,
        error: queryError?.message ?? listingsError?.message ?? null,
        filters, setFilter, setFilters, resetFilters,
        sorts, setSorts,
        page, pageSize, setPage, setPageSize, paginatedData, totalPages,
        distinctListings, distinctClients, distinctStatuses, listingLookup,
        selectedReservation, setSelectedReservation,
        savedViews, saveView, loadView, deleteView,
        viewMode, setViewMode,
        needsClientSelection,
        dataDateExtent,
      }}
    >
      {children}
    </ReservationsContext.Provider>
  );
}
